<template>
  <div class="page-container bg-surface-50 dark:bg-surface-950">
    <Navbar />

    <LoadingSpinner v-if="isLoading" is-full-page message="Loading supplier data..." />

    <main v-else class="page-content">
      <div v-if="userProfile">
        <!-- Header Section -->
        <div class="page-header">
          <div class="flex items-start justify-between mb-4">
            <div class="animate-fade-up" style="animation-fill-mode: backwards">
              <Breadcrumbs :items="[{ label: 'Supplier Dashboard' }]" />
              <h1 class="text-display-sm text-surface-900 dark:text-surface-50 mt-2 mb-2">
                Supplier Dashboard
              </h1>
              <p class="text-surface-500 dark:text-surface-400">
                Monitor UCO collection performance and supplier metrics
              </p>
            </div>
            <img
              v-if="userProfile.customer_logo && userProfile.customer_logo !== 'URL'"
              :src="userProfile.customer_logo"
              alt="Logo"
              class="h-24 w-24 object-contain rounded-2xl shadow-card border border-surface-200/60 dark:border-surface-800/60 animate-fade-in"
              style="animation-fill-mode: backwards; animation-delay: 0.1s"
            />
          </div>
        </div>

        <!-- Filters Section -->
        <div class="animate-fade-up" style="animation-delay: 0.08s; animation-fill-mode: backwards">
          <BaseCard class="mb-8 sticky top-4 z-10" elevated>
            <div class="flex items-center gap-3 mb-4">
              <div class="p-2 rounded-xl bg-accent-500/10 dark:bg-accent-500/15">
                <AdjustmentsHorizontalIcon class="h-5 w-5 text-accent-500" />
              </div>
              <h3 class="text-lg font-semibold text-surface-900 dark:text-surface-100">Filters</h3>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DateRangePicker v-model="dateRange" :max="todayIso" />
              <div>
                <label class="label">Supplier Filter:</label>
                <VueMultiselect
                  v-model="selectedSuppliers"
                  :options="supplierOptions"
                  :multiple="true"
                  :close-on-select="false"
                  placeholder="Select Suppliers"
                  label="SupplierName"
                  track-by="SupplierName"
                  class="multiselect-tailwind"
                />
              </div>
            </div>
          </BaseCard>
        </div>

        <!-- Group Tabs -->
        <div
          v-if="userProfile.group.length > 1"
          class="mb-6 animate-fade-up"
          style="animation-delay: 0.14s; animation-fill-mode: backwards"
        >
          <div class="border-b border-surface-200/60 dark:border-surface-800/60">
            <nav class="-mb-px flex space-x-1 overflow-x-auto" aria-label="Tabs">
              <button
                v-for="(group, index) in userProfile.group"
                :key="index"
                @click="transactionsIndex = index"
                :class="[
                  transactionsIndex === index
                    ? 'border-accent-500 text-accent-600 dark:text-accent-400 bg-accent-500/5 dark:bg-accent-500/10'
                    : 'border-transparent text-surface-500 hover:text-surface-700 hover:border-surface-300 dark:text-surface-400 dark:hover:text-surface-300',
                  'whitespace-nowrap py-3.5 px-5 border-b-2 font-medium text-sm transition-all duration-200 rounded-t-lg'
                ]"
              >
                {{ group.group_name }} - {{ group.site_name }}
              </button>
            </nav>
          </div>
        </div>

        <!-- Selected Group Display -->
        <div v-if="userProfile.group[transactionsIndex]" class="space-y-6">
          <!-- Group Header -->
          <div
            class="flex items-center justify-between animate-fade-up"
            style="animation-delay: 0.18s; animation-fill-mode: backwards"
          >
            <div>
              <h2 class="text-2xl font-bold text-surface-900 dark:text-surface-50">
                {{ userProfile.group[transactionsIndex].group_name }}
              </h2>
              <p class="text-surface-500 dark:text-surface-400">
                {{ userProfile.group[transactionsIndex].site_name }}
              </p>
            </div>
          </div>

          <!-- Collection Summary Section -->
          <div class="animate-fade-up" style="animation-delay: 0.22s; animation-fill-mode: backwards">
            <BaseCard elevated>
              <template #header>
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <h2 class="text-xl font-semibold text-surface-900 dark:text-surface-100 flex items-center gap-3">
                      <div class="p-2 rounded-xl bg-accent-500/10 dark:bg-accent-500/15">
                        <TableCellsIcon class="h-5 w-5 text-accent-500" />
                      </div>
                      Supplier Collection Summary
                    </h2>
                    <p class="text-sm text-surface-500 dark:text-surface-400 mt-1 ml-12">
                      Detailed breakdown of UCO collections by supplier
                    </p>
                  </div>

                  <!-- Action Buttons -->
                  <div class="flex flex-wrap gap-2">
                    <BaseButton
                      variant="secondary"
                      size="sm"
                      @click="navigateToAllTransactions"
                      :icon="EyeIcon"
                    >
                      View Details
                    </BaseButton>

                    <BaseButton
                      variant="accent"
                      size="sm"
                      @click="exportSummaryToCSV"
                      :icon="DocumentArrowDownIcon"
                    >
                      Export CSV
                    </BaseButton>
                  </div>
                </div>
              </template>

              <!-- Quick Stats -->
              <div v-if="currentGroupStats" class="mb-6">
                <QuickStats :stats="currentGroupStats" />
              </div>

              <!-- Data Table -->
              <DataTable
                v-if="filteredSummary && filteredSummary.length > 0"
                :headers="headers"
                :items="filteredSummary"
                :search-placeholder="`Search suppliers`"
                :rows-per-page="10"
                :export-filename="`supplier-summary-${startDate}-${endDate}.csv`"
              >
                <template #item-SupplierName="{ SupplierName }">
                  <span class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-accent-100 text-accent-800 dark:bg-accent-500/15 dark:text-accent-300">
                    {{ SupplierName }}
                  </span>
                </template>

                <template #item-TotalUCOCollectedFormatted="{ TotalUCOCollectedFormatted }">
                  <span class="font-semibold font-mono text-surface-900 dark:text-surface-100">
                    {{ TotalUCOCollectedFormatted }} L
                  </span>
                </template>

                <template #item-FacilitatedSavingsFormatted="{ FacilitatedSavingsFormatted }">
                  <div class="flex items-center gap-2">
                    <CloudIcon class="h-4 w-4 text-emerald-500" />
                    <span class="font-mono text-surface-700 dark:text-surface-300">
                      {{ FacilitatedSavingsFormatted }} Kg
                    </span>
                  </div>
                </template>

                <template #item-LocationName="{ LocationName }">
                  <div class="flex items-center gap-2">
                    <MapPinIcon class="h-4 w-4 text-surface-400 dark:text-surface-500" />
                    <span class="text-surface-700 dark:text-surface-300">
                      {{ LocationName }}
                    </span>
                  </div>
                </template>

                <template #empty>
                  <EmptyState
                    :icon="BeakerIcon"
                    title="No collection data"
                    message="There is no supplier collection data for the selected period. Try adjusting your date range or filters."
                    variant="info"
                  />
                </template>
              </DataTable>
            </BaseCard>
          </div>
        </div>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, watch, watchEffect } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { authFetch } from '@/composables/useApi'

import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import Breadcrumbs from '@/components/ui/Breadcrumbs.vue'
import DashboardCard from '@/components/dashboard/DashboardCard.vue'
import DateRangePicker from '@/components/dashboard/DateRangePicker.vue'
import DataTable from '@/components/ui/DataTable.vue'
import QuickStats from '@/components/ui/QuickStats.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import VueMultiselect from 'vue-multiselect'

import {
  BuildingStorefrontIcon,
  TruckIcon,
  BeakerIcon,
  CloudIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  AdjustmentsHorizontalIcon,
  MapPinIcon
} from '@heroicons/vue/24/outline'

const store = useStore()
const router = useRouter()
const { success } = useToast()

const isLoading = ref(false)
const _now = new Date()
const todayIso = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`
const dateRange = ref({
  startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 6, 2)
    .toISOString()
    .substr(0, 10),
  endDate: todayIso
})
const selectedSuppliers = ref([])
const transactionsIndex = ref(0)
const transactions = ref([])
const transactionSummary = ref([])

const userProfile = computed(() => store.state.userProfile)
const startDate = computed(() => dateRange.value.startDate)
const endDate = computed(() => dateRange.value.endDate)

const supplierOptions = computed(() => {
  const suppliers = []
  transactionSummary.value.forEach((summary) => {
    summary.forEach((transaction) => {
      if (!suppliers.some((s) => s.SupplierName === transaction.SupplierName)) {
        suppliers.push({ SupplierName: transaction.SupplierName })
      }
    })
  })
  suppliers.sort((a, b) => a.SupplierName.localeCompare(b.SupplierName))
  return suppliers
})

// Quick Stats for current group
const currentGroupStats = computed(() => {
  if (!filteredSummary.value || filteredSummary.value.length === 0) return null

  const summary = calculateSummary(filteredSummary.value)

  return [
    {
      label: 'Active Suppliers',
      value: summary.NoOfActiveSuppliers,
      icon: BuildingStorefrontIcon,
      color: '#10B981'
    },
    {
      label: 'Total UCO Collected',
      value: summary.TotalUCOCollectedFormatted + ' L',
      icon: BeakerIcon,
      color: '#059669'
    },
    {
      label: 'CO2 Savings',
      value: summary.FacilitatedSavingsFormatted + ' Kg',
      icon: CloudIcon,
      color: '#047857'
    },
    {
      label: 'Total Collections',
      value: summary.NoOfUCOCollections,
      icon: TruckIcon,
      color: '#065F46'
    }
  ]
})

const headers = [
  { value: 'SupplierName', text: 'Supplier Name', sortable: true },
  { value: 'LocationName', text: 'Location Name', sortable: true },
  { value: 'TotalUCOCollections', text: 'No. of UCO Collections', sortable: true },
  { value: 'TotalUCOCollectedFormatted', text: 'Total UCO Collected (L)', sortable: true },
  { value: 'FacilitatedSavingsFormatted', text: 'Facilitated Savings (KgCo2eq)', sortable: true },
]

const formatNumberWithSuffix = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(2) + ' Mil'
  else if (num >= 1000) return (num / 1000).toFixed(2) + ' K'
  return num.toFixed(2)
}

const fetchGroupSummary = async (apiUrl, group_id) => {
  const response = await authFetch(
    `${apiUrl}/${group_id}?start_date=${startDate.value}&end_date=${endDate.value}`,
    {},
    store.state.user
  )
  if (response.ok) {
    const result = await response.json()
    result[1].groupSummary.forEach((summary) => {
      if (summary.TotalUCOCollected !== undefined) {
        summary.FacilitatedSavings = summary.TotalUCOCollected * 0.94 * 3.00403377
        summary.TotalUCOCollectedFormatted = formatNumberWithSuffix(summary.TotalUCOCollected)
        summary.FacilitatedSavingsFormatted = formatNumberWithSuffix(summary.FacilitatedSavings)
      }
    })
    result[1].groupSummary.sort((a, b) => a.SupplierName.localeCompare(b.SupplierName))
    return result[1].groupSummary
  } else {
    console.error('Error fetching collection summary')
    return []
  }
}

const fetchTransactions = async (group_ids) => {
  isLoading.value = true
  try {
    const fetchPromises = group_ids.map(async (group_id, i) => {
      const groupData = await fetchGroupSummary(
        store.state.apiUrl + 'getSupplierCollectionSummaryByGroup',
        group_id
      )
      transactionSummary.value[i] = groupData
      transactions.value[i] = calculateSummary(groupData)
    })
    await Promise.all(fetchPromises)
  } catch (error) {
    console.error('Error fetching transactions:', error)
  } finally {
    isLoading.value = false
  }
}

const filteredTransactions = (index) => {
  if (selectedSuppliers.value.length === 0) {
    return transactionSummary.value[index]
  }
  return transactionSummary.value[index].filter((transaction) =>
    selectedSuppliers.value.some((supplier) => supplier.SupplierName === transaction.SupplierName)
  )
}

const filteredSummary = computed(() => {
  if (
    !transactionSummary.value[transactionsIndex.value] ||
    transactionSummary.value[transactionsIndex.value].length === 0
  ) {
    return []
  }
  if (selectedSuppliers.value.length === 0) {
    return transactionSummary.value[transactionsIndex.value]
  }
  return transactionSummary.value[transactionsIndex.value].filter((transaction) =>
    selectedSuppliers.value.some((supplier) => supplier.SupplierName === transaction.SupplierName)
  )
})

const calculateSummary = (transactionsData) => {
  const summary = {
    NoOfActiveSuppliers: 0,
    NoOfUCOCollections: 0,
    TotalUCOCollected: 0,
    FacilitatedSavings: 0,
    TotalUCOCollectedFormatted: '',
    FacilitatedSavingsFormatted: '',
  }

  const supplierIds = new Set()

  transactionsData.forEach((transaction) => {
    summary.NoOfUCOCollections += transaction.TotalUCOCollections || 0
    summary.TotalUCOCollected += transaction.TotalUCOCollected || 0

    if (!transaction.FacilitatedSavings) {
      transaction.FacilitatedSavings = transaction.TotalUCOCollected * 0.94 * 3.00403377
    }
    summary.FacilitatedSavings += transaction.FacilitatedSavings || 0

    supplierIds.add(transaction.SupplierId)
  })

  summary.NoOfActiveSuppliers = supplierIds.size
  summary.TotalUCOCollectedFormatted = formatNumberWithSuffix(summary.TotalUCOCollected)
  summary.FacilitatedSavingsFormatted = formatNumberWithSuffix(summary.FacilitatedSavings)

  return summary
}

const exportSummaryToCSV = () => {
  let csvContent = `Date Range:,${startDate.value} - ${endDate.value}\n`

  let supplierNames
  if (selectedSuppliers.value.length === 0) {
    supplierNames = 'All Suppliers'
  } else {
    supplierNames = selectedSuppliers.value.map((s) => s.SupplierName).join(', ')
  }
  csvContent += `Supplier Name Filter:,${supplierNames}\n\n`

  const currentTransactions = transactions.value[transactionsIndex.value]
  csvContent += 'Dashboard Summary\n'
  csvContent += 'No. of Active Suppliers,No. of UCO Collections,Total UCO Collected (L),Facilitated Savings (KgCo2eq)\n'
  csvContent += `${currentTransactions.NoOfActiveSuppliers},${currentTransactions.NoOfUCOCollections},${currentTransactions.TotalUCOCollected},${currentTransactions.FacilitatedSavings}\n\n`

  csvContent += 'Collection Summary Table\n'
  const headerRow = headers.map((h) => h.text).join(',')
  csvContent += headerRow + '\n'

  filteredSummary.value.forEach((transaction) => {
    const row = [
      transaction.SupplierName,
      transaction.LocationName,
      transaction.TotalUCOCollections,
      transaction.TotalUCOCollected,
      transaction.FacilitatedSavings,
    ]
    csvContent += row.map((value) => `"${value}"`).join(',') + '\n'
  })

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'collection_summary.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    success('Summary exported successfully!')
  }
}

const navigateToAllTransactions = () => {
  if (userProfile.value.group[transactionsIndex.value]) {
    router.push({
      name: 'AllTransactionsSupplier',
      params: {
        group_id: userProfile.value.group[transactionsIndex.value].group_id,
      },
    })
  }
}

watchEffect(() => {
  if (userProfile.value) {
    const groupIds = userProfile.value.group.map((group) => group.group_id)
    fetchTransactions(groupIds)
  }
})

watch([startDate, endDate], () => {
  const groupIds = userProfile.value.group.map((group) => group.group_id)
  fetchTransactions(groupIds)
})

watch(selectedSuppliers, () => {
  transactions.value = userProfile.value.group.map((group, index) =>
    calculateSummary(filteredTransactions(index))
  )
})
</script>

<style scoped>
/* Tab hover lift effect */
nav button {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

nav button:hover {
  transform: translateY(-1px);
}
</style>
